/**
 * Cookie Consent Manager deals with data privacy and cookie settings
 *
 * Usage: ```
 * function initServices(event) {
 *   let consent = event.details;
 *
 *   // ... initialize stuff based on consent or window.consentManager.allows('{category}.{detail}') ...
 * }
 * consentManager.init({
 *   // @see cr.init() ... initialize with options, call this before any other consent manager function
 * });
 * ```
 *
 * Whenever iframes should be blocked, make sure to configure the Cookie Consent
 * Manager the way it works with i.e. the existing *iframe-youtube* type:
 *
 * 1. Embed the iframe with `data-cookie-type="iframe-{name} data-src="https://example.at"`
 *    * ensure a container div / DOM element
 * 2. Within your `initServices` callback:
 *    * call `consentManager.services.iframes.showBlockingMsg('iframe-{name}');`
 *    * check for permissions and init if permissions were granted by calling `consentManager.services.iframes.init('iframe-youtube', { ... });`
 * 3. Make sure to add the required labels to `cr.lang` / `consentManager.lang`
 *    * (a) from the calling code (best-practice)
 *    * (b) within this file
 *
 * Custom services: Adding custom services works by specifying the service with
 * a *custom-* prefix:
 *
 * 1. Add the service to one of the categories on `consentManager.init`, i.e. *tracking.custom-abc*
 * 2. Add lang variables for the new service and all languages available
 * ```
 *   consentManager.lang.de = $.extend(consentManager.lang.de, {
 *     cr_form_service_custom_abc_label: "ABC",
 *     cr_form_service_custom_abc_description: "Anbieter: ABC<br>Beschreibung: Setzt ein Beispiel-Cookie, das für das ABC Skript benötigt wird.<br>Speicherdauer: 1 Jahr",
 *   });
 * ```
 * 3. Within your `initServices` callback check if the service has permission an do as you want if so..
 *
 * @requires https://github.com/jquery/jquery
 * @requires https://github.com/js-cookie/js-cookie
 *
 * $LastChangedDate: 2023-07-12 15:19:34 +0200 (Mi., 12 Jul 2023) $
 * $LastChangedBy: jua $
 *
 * @package EDWIN Frontend
 * @author Raphael Posch, Benjamin Ulmer
 * @copyright (c) 2020 Q2E GmbH
 */


/**
 * CustomEvent support for IE11
 * https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent#Polyfill
 */
(function () {

    if (typeof window.CustomEvent === 'function') {
        return false;
    }

    function CustomEvent(event, params) {
        params = params || { bubbles: false, cancelable: false, detail: null };

        let evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);

        return evt;
    }

    window.CustomEvent = CustomEvent;

})();

/**
 * Consent Manager
 */
(function ($, Cookies) {

    if ($ === undefined) {
        throw 'Missing depencency: jQuery. Please make sure to include jQuery (https://jquery.com/) before this script.';
    }

    if (Cookies === undefined) {
        throw 'Missing depencency: Cookies. Please make sure to include Cookies (https://github.com/js-cookie/js-cookie) before this script.';
    }

    let cr = {}, // public scope namespace
        priv = {}; // private scope namespace

    cr.CATEGORY_NECESSARY = 'necessary';
    cr.CATEGORY_FUNCTIONAL = 'functional';
    cr.CATEGORY_TRACKING = 'tracking';
    cr.CATEGORY_MARKETING = 'marketing';

    cr.COOKIE_NAME = 'q2e_consent_manager';

    cr.lang = {
        de: {
            cr_form_service_iframe_youtube_label: "Youtube",
            cr_form_service_iframe_youtube_description: "Anbieter: Google<br>Beschreibung: Wir nutzen YouTube im erweiterten Datenschutzmodus, in dem keine Cookies gesetzt werden. Bei der Wiedergabe eines Videos werden nur Verbindungsdaten an Google übertragen.<br>Speicherdauer: keine",
            cr_content_google_maps_blocked_text: "Die Cookie-Einstellungen verhindern das Laden von Google Maps. Bitte prüfen Sie Ihre Einstellungen.",
            cr_content_google_maps_blocked_button_label: "Cookie Einstellungen ändern",
            cr_content_iframe_youtube_blocked_text: "Die Cookie-Einstellungen verhindern das Laden von Youtube Videos. Bitte prüfen Sie Ihre Einstellungen.",
            cr_content_iframe_youtube_blocked_button_label: "Cookie Einstellungen ändern"
        },
        en: {
            cr_form_service_iframe_youtube_label: "Youtube",
            cr_form_service_iframe_youtube_description: "Provider: Google<br>Description: We use YouTube in the extended data protection mode, in which no cookies are set. When playing a video, only connection data is transmitted to Google.<br>Storage time: none",
            cr_content_google_maps_blocked_text: "The cookie settings are blocking Google Maps. Please check your cookie settings.",
            cr_content_google_maps_blocked_button_label: "Change cookie settings",
            cr_content_iframe_youtube_blocked_text: "The cookie settings are blocking Youtube videos. Please check your cookie settings.",
            cr_content_iframe_youtube_blocked_button_label: "Change cookie settings"
        }
    };

    // a gobale store for external meta information when initializing something
    // like youtube and others (see cr.tools.* functions below)
    cr.initStatusObj = {};

    priv.cookieConsent = {};
    priv.options = {};

    /***
     * Checks if cookies for given category are allowed
     *
     * @param {String} feature, use one of the CATEGORY_* constants to check
     *                 generally, and use i.e. functional.resolution,
     *                 tracking.analytics, ... to fetch for feature directly
     *
     * @returns {Boolean}
     *
     * @throws Exception for invalid parameter 'feature'
     */
    cr.allows = function (feature) {
        let category,
            detail;

        if (!$.trim(feature)) {
            throw "Invalid parameter 'feature' for method cr.allows(feature). Please provide a category (i.e. 'functional') or the feature in format '{category}.{detail}' (i.e. 'functional.resolution').";
        }

        feature = feature.split('.');
        category = $.trim(feature[0]);
        detail = $.trim(feature[1]);

        if (!category) {
            throw "Invalid parameter 'feature' for method cr.allows(feature). Please provide a category (i.e. 'functional') or the feature in format '{category}.{detail}' (i.e. 'functional.resolution').";
        }

        // if the requested catgory is necessary cookie we return true always, because
        // necessary cookies are used anyway
        if (category === cr.CATEGORY_NECESSARY) {
            return true;
        }

        if (priv.cookieConsent[category] === undefined) {
            return false;
        }

        if (detail) {
            return priv.cookieConsent[category].indexOf(detail) >= 0;
        }

        return priv.cookieConsent[category].length > 0;
    };

    /**
     * Checks all the cookie checkboxes
     */
    cr.checkAll = function () {
        $('.mod-cr-control-form input[type=checkbox]').prop('checked', true);
    };

    /**
     * Unchecks all the cookie categories except the necessary (=disabled) only
     */
    cr.uncheckAll = function () {
        $('.mod-cr-control-form input[type=checkbox]')
            .not('[disabled]')
            .prop('checked', false);
    };

    /**
     * Initialize the consent manager
     *
     * @param {{}} opts
     *
     * - debug: false|true, displays a button to trigger the consent manager again
     * - events
     *  - onChanged: function(event) {}, added as 'cr-consent-manager-changed' event handler
     *  - onInit: function(event) {}, added as 'cr-consent-manager-init' event handler
     * - hidden: false|true, default: false, set to true to hide consent manager
     *           by default
     * - services: {
     *     necessary: ['website', 'consent-manager'],
     *     functional: ['resolution', 'facebook-sdk', 'google-maps', 'iframe-youtube'],
     *     tracking: ['google-analytics', 'google-tag-manager'],
     *     marketing: ['google-ads', 'facebook-pixel', 'linkedin-insights']
     *   } if set only the defined consent options for given services will
     *     be displayed. Use this option to modify consent manager form without
     *     direct template modifications.
     * - cacheVersion: '{main_cache_consentmanager_version}', increase the
     *     variable so that the consent manager is called up again when there is a change
     */
    cr.init = function (opts) {
        priv.options = $.extend(true, {
            debug: false,
            events: {
                onChanged: function () { },
                onInit: function () { }
            },
            hidden: false,
            cacheVersion: '',
            lang: 'de',
            services: null
        }, opts);

        cr.COOKIE_NAME = cr.COOKIE_NAME + priv.options.cacheVersion;

        priv.preInit();

        cr.initStatus();

        priv.initBasics();
        priv.initMainButtons();
        priv.initDetails();

        if (priv.options.hidden) {
            cr.hide();
        }
    };

    /**
     * Initialize stuff before starting the actual internal init processes, depends
     * on given options
     */
    priv.preInit = function () {

        if (cr.lang[priv.options.lang] === undefined) {
            throw "Invalid option 'lang'. Language '" + priv.options.lang + "' is not available.";
        }

        // disable services not required if a services configuration is provided

        if (priv.options.services) {
            $('#mod-cr-settings-form input[type=checkbox]').each(function () {
                let $this = $(this);

                if ($this.data('category-toggle')) {
                    if (priv.options.services[$this.data('category-toggle')] === undefined) {
                        $('#mod-cr-category-' + $this.data('category-toggle')).remove();
                    }
                }
                else if ($this.data('category-cookie-toggle')) {
                    if ($.isArray(priv.options.services[$this.data('category')])
                        && priv.options.services[$this.data('category')].indexOf($this.data('category-cookie-toggle')) === -1
                    ) {
                        $this.parent().parent().remove();
                    }
                }
            });
        }

        // dynamically add custom-* + iframe-* services
        (function(services) {
            let types = ['iframe', 'custom'];

            for (let t = 0; t < types.length; t++) {
                let type = types[t];

                for (let i in services) {
                    let $categoryContainer = $('#mod-cr-settings-form [data-category-toggle="' + i + '"]')
                        .parents('.mod-cr__category')
                        .find('.mod-cr__details');

                    for (let j = 0; j < services[i].length; j++) {
                        let service = services[i][j];

                        if (service.indexOf(type + '-') !== 0) {
                            continue;
                        }

                        if (cr.lang[priv.options.lang]['cr_form_service_' + service.replaceAll('-', '_') + '_label'] === undefined) {
                            throw "Please provide a label for service '" + service + "'.";
                        }
                        else if (cr.lang[priv.options.lang]['cr_form_service_' + service.replaceAll('-', '_') + '_description'] === undefined) {
                            throw "Please provide a description for service '" + service + "'.";
                        }

                        let html = $('[data-role="tpl-service"]').html();

                        html = html
                            .replaceAll(':category:', i)
                            .replaceAll(':service:', service)
                            .replaceAll(':service_label:', cr.lang[priv.options.lang]['cr_form_service_' + service.replaceAll('-', '_') + '_label'])
                            .replaceAll(':service_description:', cr.lang[priv.options.lang]['cr_form_service_' + service.replaceAll('-', '_') + '_description']);

                        $categoryContainer.append(html);
                    }
                }
            }
        })(priv.options.services);

        // start registering event handler's provided directly via options

        $(document).on('cr-consent-manager-changed', function (event) {
            priv.options.events.onChanged(event);

            if (priv.options.debug) {
                console.log('cr:event cr-consent-manager-changed');
                console.log('cr:selected options', event.detail);
                console.log('cr:available options', cr.getConsentOptions());
            }
        });

        $(document).on('cr-consent-manager-init', function (event) {
            priv.options.events.onInit(event);

            if (priv.options.debug) {
                console.log('cr:event cr-consent-manager-init');
                console.log('cr:selected options', event.detail);
                console.log('cr:available options', cr.getConsentOptions());
            }
        });

        // show a button to toggle the consent manager in debug mode

        if (!priv.options.debug) {
            return;
        }

        $('body').append(
            '<button data-role="cr-debug-toggle" class="mod-cr-debug-toggle">Consent Manager</button>'
        );
    };

    /**
     * Check if the q2e cookie consent is set - if not (or empty), show the banner
     */
    cr.initStatus = function () {
        Object.keys(Cookies.get()).forEach(key => {
            if (key.indexOf('q2e_consent_manager') > -1 && key !== cr.COOKIE_NAME) {
                Cookies.remove(key);
            }
        });

        let value = Cookies.get(cr.COOKIE_NAME),
            i = 0,
            cookieKey,
            $form = $('.mod-cr-control-form');

        // if no cookies are stored yet, we store an empty object
        // and continue
        if (value === undefined) {
            priv.storeCookieData({});
            value = Cookies.get(cr.COOKIE_NAME);
        }

        // if data is an empty object, this indicates that the user has not made his
        // choice yet, so we ask him again
        if (value === '{}') {
            cr.show();
        }

        priv.cookieConsent = JSON.parse(value);
        for (cookieKey in priv.cookieConsent) {

            // no object = nothing allowed
            if (!priv.cookieConsent.hasOwnProperty(cookieKey)
                || !$.isArray(priv.cookieConsent[cookieKey])
                || !priv.cookieConsent[cookieKey].length
            ) {
                continue;
            }

            // init category checkboxes
            $form.find('[data-category-toggle=' + cookieKey + ']').prop('checked', true);

            // init category details checkboxes by clearing and setting again
            // the selected values
            $form.find('[data-category=' + cookieKey + ']').prop('checked', false);
            for (i = 0; i < priv.cookieConsent[cookieKey].length; i++) {
                $form
                    .find('[data-category=' + cookieKey + '][data-category-cookie-toggle=' + priv.cookieConsent[cookieKey][i] + ']')
                    .prop('checked', true);
            }
        }

        window.document.dispatchEvent(new CustomEvent('cr-consent-manager-init', {
            detail: $.extend({}, priv.cookieConsent) // send a deep copy for event, to avoid original data modification
        }));
    };

    /**
     * Stores cookie settings via selected form values and trigger the
     * 'cr-consent-manager-changed' event
     *
     * @see priv.storeCookies()
     */
    cr.store = function () {
        priv.storeCookies();

        window.document.dispatchEvent(new CustomEvent('cr-consent-manager-changed', {
            detail: $.extend({}, priv.cookieConsent) // send a deep copy for event, to avoid original data modification
        }));
    };

    /**
     * Sets the consent for give cookie category
     *
     * @param {String} category
     *
     * @param {Boolean} bool
     */
    cr.setConsent = function (category, bool) {
        $('.mod-cr-control-form #' + category).prop('checked', bool);
    };

    /**
     * Gets the consent form status for category
     *
     * @param {String} category
     *
     * @return {Boolean}
     */
    cr.getConsent = function (category) {
        return $('.mod-cr-control-form #' + category).prop('checked');
    };

    /**
     * Returns an object of consent options (= available categories and details).
     * Can be used for debugging during development.
     *
     * @return {{}}
     */
    cr.getConsentOptions = function () {
        let options = {};

        $('.mod-cr-control-form [data-category-toggle]').each(function () {
            let category = $(this).data('category-toggle');

            options[category] = [];

            $('.mod-cr-control-form [data-category=' + category + ']').each(function () {
                options[category].push($(this).data('category-cookie-toggle'));
            });
        });

        return options;
    };

    /**
     * Show the manager
     */
    cr.show = function (focus) {
        $('.mod-cr').css('display', 'block');

        cr.trapFocus();

        if (focus === true) {
            $('.mod-cr .mod-cr-control-form .mod-cr-accept-all').focus();
        }
    };

    /**
     * Hide the manager
     */
    cr.hide = function () {
        $('.mod-cr').hide(100);
        $('#mod-cr-settings-form').css('display', 'none');
    };

    /**
     * trap focus in manager
     */
    cr.trapFocus = function () {
        const KEYCODE_TAB = 9;
        let $modal = $(".mod-cr");
        let $main = $(".mod-cr__main");
        let $cookieSettings = $(".mod-cr .mod-cr__settings");
        let $modalFocusable = $modal.find('a[href], input:not([disabled], .visually-hidden), button:not([disabled]), [tabindex="0"]');
        let $mainFocusable = $main.find('a[href], button:not([disabled]), [tabindex="0"]');

        $modal.focus();

        $modal.on("keydown", function (e) {
            let isTabPressed = e.key === "Tab" || e.keyCode === KEYCODE_TAB;

            if (!isTabPressed) {
                return;
            }

            let $allFocusableElements = $mainFocusable;

            if ($cookieSettings.is(':visible')) {
                $allFocusableElements = $modalFocusable;
            }

            let firstFocusableElement = $allFocusableElements[0];
            let lastFocusableElement = $allFocusableElements[$allFocusableElements.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusableElement) {
                    firstFocusableElement.focus();
                    e.preventDefault();
                }
            }
        });
    }

    // helper functions from here
    cr.tools = {};

    /**
     * Loads a script
     *
     * @param {String} src
     * @param {Boolean} defer (optional)
     * @param {Boolean} async (optional)
     * @param {String} crossorigin (optional)
     * @param {String} nonce (optional)
     * @param {String} callback (optional)
     */
    cr.tools.loadScript = function (src, defer, async, crossorigin, nonce, callback) {

        let scriptTag = document.createElement('script'), // create a script tag
            firstScriptTag = document.getElementsByTagName('script')[0]; // find the first script tag in the document

        scriptTag.src = src; // set the source of the script to your script

        if (defer) {
            scriptTag.setAttribute('defer', '');
        }

        if (async) {
            scriptTag.setAttribute('async', '');
        }

        if (crossorigin !== undefined && crossorigin.length) {
            scriptTag.setAttribute('crossorigin', crossorigin);
        }

        if (nonce !== undefined && nonce.length) {
            scriptTag.setAttribute('nonce', nonce);
        }

        if (callback !== undefined) {
            scriptTag.onload = function () {
                callback();
            }
        }

        firstScriptTag.parentNode.insertBefore(scriptTag, firstScriptTag); // append the script to the DOM
    };

    //
    // functions to enable various services on demand
    //

    cr.services = {};

    cr.services.google = {};

    /**
     * @deprectated Use GA4 instead, cr.services.google.initGA4()
     *
     * Initializes Google Analytics by including the analytics.js script and
     * creating the instance.
     *
     * NOTE: all actions have to be taken via the 'callback' function. If not
     * provided, the 'pageview' is triggered automatically (ip anonymized by default).
     *
     * @param {String} id
     * @param {{}} opts
     *  - name: the tracker name, default: q2eTracker
     *  - callback: the callback function to initialize Google Analytics settings and start tracking
     */
    cr.services.google.initGA = function (id, opts) {
        let options = $.extend({
            name: 'q2eTracker',
            callback: function () {
                window[options.name]('set', 'anonymizeIp', true);
                window[options.name]('send', 'pageview');
            }
        }, opts || {});

        if (options.name === undefined) {
            options.name = 'q2eTracker';
        }

        (function (i, s, o, g, r, a, m) {
            i['GoogleAnalyticsObject'] = r;
            i[r] = i[r] || function () {
                (i[r].q = i[r].q || []).push(arguments)
            }, i[r].l = 1 * new Date();
            a = s.createElement(o),
                m = s.getElementsByTagName(o)[0];
            a.async = 1;
            a.src = g;
            m.parentNode.insertBefore(a, m)
        })(window, document, 'script', '//www.google-analytics.com/analytics.js', options.name);

        window[options.name]('create', id, 'auto');

        options.callback();
    };

    /**
     * Init GA4 (gtag.js)
     *
     * @see https://developers.google.com/analytics/devguides/migration/ua/analyticsjs-to-gtagjs
     *      for further information on migrating from analytics.js to gtag.js
     * @see https://developers.google.com/analytics/devguides/migration/measurement/virtual-pageviews
     *      migrate virtual page views from analytics.js to gtag.js
     *
     * @param id
     * @param opts
     *        - callback: function(id) {}, the callback to call after this script
     *                    was initialized, param 'id' is the tracking ID, use
     *                    `gtag()` to call functions inside the callback
     */
    cr.services.google.initGA4 = function(id, opts) {
        let options = $.extend({
            callback: function(id) {
                //
                // Turn off all advertising features if the user did not consent to
                // marketing cookies
                // @see https://developers.google.com/tag-platform/devguides/privacy#turn_off_all_advertising_features
                //
                // example:
                // gtag('set', 'allow_google_signals', consentManager.allows('marketing.google-ads'));
            }
        }, opts || {});

        cr.tools.loadScript('https://www.googletagmanager.com/gtag/js?id=' + id);

        window.dataLayer = window.dataLayer || [];

        window.gtag = function() {
            dataLayer.push(arguments);
        };

        gtag('js', new Date());

        // automatically sends the page view, no extra call required
        gtag('config', id);

        options.callback(id);
    };

    /**
     * Initialize the google tag manager
     *
     * @param {String} id
     * @param {{}} opts
     *  - callback: the callback function, called as soon as initialized
     *
     * @see https://tagmanager.google.com
     */
    cr.services.google.initGTM = function (id, opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
            });
            let f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src =
                'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', id);

        options.callback();
    };


    cr.services.pinterest = {};

    /**
     *
     * Initializes Pinterest tracking
     *
     * @param {String} id
     * @param {string} email
     */

    cr.services.pinterest.init = function (id) {
        !function (e) {
            if (!window.pintrk) {
                window.pintrk = function () {
                    window.pintrk.queue.push(Array.prototype.slice.call(arguments))
                };

                var n = window.pintrk; n.queue = [], n.version = "3.0";
                var t = document.createElement("script"); t.async = !0, t.src = e;
                var r = document.getElementsByTagName("script")[0];

                r.parentNode.insertBefore(t, r)
            }
        }("https://s.pinimg.com/ct/core.js");

        pintrk('load', id);
        pintrk('page');

    };

    cr.services.matomo = {};

    /**
     *
     * Initializes Matomo tracking
     *
     * @param {String} matomoDomain
     * @param {String} siteId
     */

    cr.services.matomo.init = function(matomoDomain, siteId) {
        if (!matomoDomain) {
            return;
        }

        var _paq = window._paq = window._paq || [];
        /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
        _paq.push(['trackPageView']);
        _paq.push(['enableLinkTracking']);
        (function() {
            var u="//"+ matomoDomain +"/";
            _paq.push(['setTrackerUrl', u+'matomo.php']);
            _paq.push(['setSiteId', siteId]);
            var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
            g.type='text/javascript'; g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
        })();
    };

    cr.services.facebook = {};

    /**
     * Initialize the facebook pixel
     *
     * @param {String} id
     * @param {{}} opts
     *  - callback: the callback function, called as soon as initialized
     *
     * @see https://developers.facebook.com/docs/facebook-pixel/implementation
     */
    cr.services.facebook.initPixel = function (id, opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        !function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            };
            if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
            n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s)
        }(window, document, 'script',
            'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', id);
        fbq('track', 'PageView');

        options.callback();
    };

    /**
     * Initialize the facebook SDK
     *
     * @param {String} appId, the facebook app id
     * @param {{}} opts
     *  - callback: the callback function, called as soon as initialized
     */
    cr.services.facebook.initSdk = function (appId, opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        (function (d, s, id) {
            var js, fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;
            js = d.createElement(s); js.id = id;
            js.src = "//connect.facebook.net/de_DE/sdk.js#xfbml=1&version=v7.0&appId=" + appId;
            fjs.parentNode.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));

        options.callback();
    };

    cr.services.linkedin = {};

    /**
     * Initialize the linkedin insights
     *
     * @param {String} id
     * @param {{}} opts
     *  - callback: the callback function, called as soon as initialized
     *
     * @see https://business.linkedin.com/de-de/marketing-solutions/insight-tag#get-started
     * @see https://www.linkedin.com/help/lms/answer/65513
     */
    cr.services.linkedin.initInsights = function (id, opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        _linkedin_partner_id = id;
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);
        (function () {
            var s = document.getElementsByTagName("script")[0];
            var b = document.createElement("script");
            b.type = "text/javascript"; b.async = true;
            b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
            s.parentNode.insertBefore(b, s);
        })();

        options.callback();
    };

    /**
     * Google Maps iframe handling
     */

    cr.services.google.maps = {};

    cr.services.google.maps.showBlockingMsg = function(opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        $('[data-cookie-type="google-maps"]').each(function() {
            let $this = $(this);

            // if src is set we already initialized Google Maps or if the overlay was
            // already created, we do not need to block it - stop here
            if (   ($this.attr('src') !== '' && $this.attr('src') !== undefined)
                || $this.parent().find('.js-map_overlay').length > 0
            ) {
                return;
            }

            $this.before(
                '<div class="js-map_overlay mod-cr-overlay">' +
                '  <p>' + cr.lang[priv.options.lang].cr_content_google_maps_blocked_text + '</p>' +
                '  <a href="#" onclick="window.consentManager.show(true);return false">' + cr.lang[priv.options.lang].cr_content_google_maps_blocked_button_label + '</a>' +
                '</div>'
            );

            $this.addClass('invisible').attr('tabindex', '-1');
        });

        options.callback();
    }

    cr.services.google.maps.init = function(opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        $('[data-cookie-type="google-maps"]').each(function() {
            let $this = $(this);

            if ($this.attr('src') !== '' && $this.attr('src') !== undefined) {
                return;
            }

            $this.removeClass('invisible')
                .attr('src', $this.attr('data-src'))
                .removeAttr('tabindex')
                .parent().find('.js-map_overlay').remove();
        });

        options.callback();
    }

    /**
     * Handling of any iframes (blocking + activation)
     * NOTE: can be used for any services of type *iframe-{name}*
     */

    cr.services.iframes = {};

    cr.services.iframes.showBlockingMsg = function(service, opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        if (cr.lang[priv.options.lang]['cr_content_' + service.replaceAll('-', '_') + '_blocked_text'] === undefined) {
            throw "Can not create overlay. Please provide a text for service '" + service + "'.";
        }
        else if (cr.lang[priv.options.lang]['cr_content_' + service.replaceAll('-', '_') + '_blocked_button_label'] === undefined) {
            throw "Can not create overlay. Please provide a button label for service '" + service + "'.";
        }

        $('[data-cookie-type="' + service + '"]').each(function() {
            let $this = $(this);

            // if src is set we already initialized the iframe or if the overlay was
            // already created, we do not need to block it - stop here
            if (   ($this.attr('src') !== '' && $this.attr('src') !== undefined)
                || $this.parent().find('.js-iframe_overlay').length > 0
            ) {
                return;
            }

            $this.before(
                '<div class="js-iframe_overlay mod-cr-overlay">' +
                '  <p>' + cr.lang[priv.options.lang]['cr_content_' + service.replaceAll('-', '_') + '_blocked_text'] + '</p>' +
                '  <a href="#" onclick="window.consentManager.show(true);return false">' + cr.lang[priv.options.lang]['cr_content_' + service.replaceAll('-', '_') + '_blocked_button_label'] + '</a>' +
                '</div>'
            );

            $this.addClass('invisible').attr('tabindex', '-1');
        });

        options.callback();
    }

    cr.services.iframes.init = function(service, opts) {
        let options = $.extend({
            callback: function () { }
        }, opts || {});

        $('[data-cookie-type="' + service + '"]').each(function() {
            let $this = $(this);

            if ($this.attr('src') !== '' && $this.attr('src') !== undefined) {
                return;
            }

            $this.removeClass('invisible')
                .attr('src', $this.attr('data-src'))
                .removeAttr('tabindex')
                .parent().find('.js-iframe_overlay').remove();
        });

        options.callback();
    }

    /* NOTE: Additional functions for initializing further services not
             implemented yet.

    // YOUTUBE {{{
    cr.youtube = new Object();
    cr.youtube.showBlockingMsg = function() {
        // {{{
        let youtubeFrames = window.document.querySelectorAll('iframe[data-src^="https://www.youtube"]');
        let parentWindow = window;
        let msg = $.get("/path/to/script/blocked-msg-youtube", null, function(data) {
            youtubeFrames.forEach(function(ye) {
                ye.contentDocument.body.innerHTML = msg.responseText;
                ye.contentDocument.body.addEventListener('click', function(ev) {
                    let closest;

                    closest = event.target.closest(".cr-cookie-control-allow-youtube-once");
                    if (closest && this.contains(closest)) {
                        ev.preventDefault();
                        ye.src = ye.dataset.src;
                    }

                    closest = event.target.closest(".cr-cookie-control-show");
                    if (closest && this.contains(closest)) {
                        ev.preventDefault();
                        ye.contentWindow.parent.consentManager.show();
                    }

                    closest = event.target.closest(".cr-cookie-control-show-details");
                    if (closest && this.contains(closest)) {
                        ye.contentWindow.parent.consentManager.showDetails();
                    }

                });
                ye.contentDocument.getElementById('cr-cookie-control-allow-youtube').addEventListener('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    parentWindow.consentManager.setConsent("youtube", true);
                    parentWindow.consentManager.store();
                });
            });
        });
        // }}}
    }
    cr.youtube.init = function() {
        // {{{
        if (window.consentManager.initStatusObj.youtube !== true) {
            let youtubeFrames = window.document.querySelectorAll('iframe[data-src^="https://www.youtube"]');
            youtubeFrames.forEach(function(e) {
                e.src = e.dataset.src;
            });
            window.consentManager.initStatusObj.youtube = true;
        } else {
            console.log("Cookie consent for Youtube: already initialized");
        }
        // }}}
    }
    // }}}

    cr.services.google.initGTM = function(id) {
        // {{{
        cr.tools.loadScript("https://www.googletagmanager.com/gtag/js?id=" + id);
        window.dataLayer = window.dataLayer || [];

        function gtag() {
            dataLayer.push(arguments);
        }
        gtag("js", new Date());

        gtag("config", id);
        // }}}
    }

    cr.services.google.initLogin = function() {
        cr.tools.loadScript("https://apis.google.com/js/client:platform.js?onload=googleInit", true, true, "", "", function() {
            window.setTimeout(function(){
                console.log($("div#google-container"));
                $("div#google-container").remove();
                $("#myGoogleButton").show();
            }, 300);
        });

    }
    cr.services.google.login_showBlockingMsg = function() {
        let socialSigninContainer = $("#myGoogleButton");
        let parentWindow = window;
        let msg = $.get("/path/to/script/blocked-msg-social-signin", null, function(data) {
            //console.log(data);
            let googleContainer = $(data).filter("div#google-container");
            if($("div#google-container").length == 0) {
                socialSigninContainer.hide();
                socialSigninContainer.after(googleContainer);

                $(".cr-cookie-control-show").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    parentWindow.consentManager.show();
                    parentWindow.consentManager.showDetails();
                });

                $("#cr-cookie-control-allow-google-login").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    $("div#google-container").remove();
                    parentWindow.consentManager.setConsent("google-login", true);
                    parentWindow.consentManager.store();
                });
            }
        });
    }

    //GOOGLE RECAPTCHA BLOCKING MESSAGE AND INIT
    //{{{
    cr.services.google.RC = new Object();
    cr.services.google.RC.init = function() {
        // {{{
        cr.tools.loadScript("https://www.google.com/recaptcha/api.js", true, true);

        //to-do unhide frame with customer reviews
        let blockedFrame = $("#-js-block-message-rc");
        let customerReview = $("#-js-review-form");
        customerReview.show();
        blockedFrame.hide();
        // }}}
    }
    cr.services.google.RC.showBlockingMsg = function() {
        // {{{
        let div = $("#-js-block-message-rc");

        // to-do hide divs with customer reviews
        let customerReview = $("#-js-review-form");
        customerReview.hide();
        if (div.length == 0) {
            let newFrame = $("<div/>", {
                "id": "-js-block-message-rc"
            });
            customerReview.parent().prepend(newFrame);
            div = $("#-js-block-message-rc");
        }

        let parentWindow = window;
        if (div.length != 0) {
            let msg = $.get("/path/to/script/blocked-msg-recaptcha", null, function(data) {
                div.html(msg.responseText);

                $("#cr-cookie-control-edit-cookies").on('click', function(ev) {
                    ev.preventDefault();
                    console.log(parentWindow.consentManager);
                    parentWindow.consentManager.show();
                    parentWindow.consentManager.showDetails();
                });

                $("#cr-cookie-control-allow-google").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    parentWindow.consentManager.setConsent("google", true);
                    parentWindow.consentManager.store();
                });
            });
        }
        // }}}
    }
    // }}}

    //GOOGLE MAPS BLOCKING MESSAGE AND INIT
    //{{{
    cr.services.google.maps = new Object();
    cr.services.google.maps.showBlockingMsg = function() {
        let mapDefault = $("iframe[data-src^='https://www.google.com/maps']");
        let mapAlternate = $("iframe[data-src^='https://maps.google.at/maps']");
        let mapFrameBlocked = (mapDefault.length > 0) ? mapDefault : mapAlternate;
        let parentWindow = window;
        //add blocking message
        if (mapFrameBlocked.length !== 0) {
            let msg = $.get("/path/to/script/blocked-msg-googlemaps", null, function(data) {
                mapFrameBlocked[0].contentDocument.body.innerHTML = msg.responseText;

                mapFrameBlocked[0].contentDocument.getElementById("cr-cookie-control-edit-cookies").addEventListener('click', function(ev) {
                    ev.preventDefault();
                    mapFrameBlocked[0].contentWindow.parent.consentManager.show();
                    mapFrameBlocked[0].contentWindow.parent.consentManager.showDetails();
                });

                mapFrameBlocked[0].contentDocument.getElementById("cr-cookie-control-allow-googlemaps").addEventListener('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    parentWindow.consentManager.setConsent("google-maps", true);
                    parentWindow.consentManager.store();
                });
            });
        }
    }
    cr.services.google.maps.init = function() {
        if (window.consentManager.initStatusObj.googleMaps !== true) {
            let mapDefault = $("iframe[data-src^='https://www.google.com/maps']");
            let mapAlternate = $("iframe[data-src^='https://maps.google.at/maps']");
            let mapFrameBlocked = (mapDefault.length > 0) ? mapDefault : mapAlternate;
            mapFrameBlocked.attr("src", mapFrameBlocked.attr("data-src"));
            window.consentManager.initStatusObj.googleMaps = true;
        } else {
            console.log("Cookie consent for GoogleMaps: already initialized");
        }
    }
    //}}}

    //MAIL CHIMP PREVENT REDIRECT AND INIT
    cr.mailchimp = new Object();
    cr.mailchimp.form = $("#mc-embedded-subscribe-form");
    cr.mailchimp.showBlockingMsg = function() {
        // {{{
        let container = $("#mc-field-container");
        let parentWindow = window;
        if (cr.mailchimp.form.length != 0) {
            let msg = $.get("/path/to/script/blocked-msg-mailchimp", null, function(ev) {
                container.attr("data-html", container[0].innerHTML);
                container.html(msg.responseText);

                $("#cr-cookie-control-edit-cookies").on('click', function(ev) {
                    ev.preventDefault();
                    parentWindow.consentManager.show();
                    parentWindow.consentManager.showDetails();
                });

                $("#cr-cookie-control-allow-mailchimp").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    parentWindow.consentManager.setConsent("mailchimp", true);
                    parentWindow.consentManager.store();
                });
            });
        }
        // }}}
    }
    cr.mailchimp.init = function() {
        // {{{
        if (window.consentManager.initStatusObj.mailchimp !== true) {
            let container = $("#mc-field-container");
            window.setTimeout(function() {
                container.html(container.attr("data-html"));
            }, 1000);
            window.consentManager.initStatusObj.mailchimp = true;
        } else {
            console.log("Cookie consent for Mailchimp: already initialized");
        }
        // }}}
    }


    //FACEBOOK
    cr.facebook = new Object();
    cr.facebook.init = function(id) {
        // {{{
        if (window.consentManager.initStatusObj.facebook !== true) {
            ! function(f, b, e, v, n, t, s) {
                if (f.fbq) return;
                n = f.fbq = function() {
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = !0;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s)
            }(window, document, 'script',
                'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', id);
            fbq('track', 'PageView');
            window.consentManager.initStatusObj.facebook = true;
        } else {
            console.log("Cookie consent for Facebook Pixel: already initialized");
        }
        // }}}
    }
    cr.facebook.initLogin = function() {
        //to-do add initialize code for facebook login
        cr.tools.loadScript("https://connect.facebook.net/de_DE/sdk.js#xfbml=1&autoLogAppEvents=1&version=v7.0&appId=608889990011384", true, true, "anonymous", "4GQWwWIt", function() {
            window.setTimeout(function(){
                console.log($("div#facebook-container"));
                $(".fb-login-button").show();
                $("div#facebook-container").remove();
            }, 300);
        });
        initFacebook();
    }
    cr.facebook.login_showBlockingMsg = function() {
        let socialSigninContainer = $(".fb-login-button");
        let parentWindow = window;
        let msg = $.get("/path/to/script/blocked-msg-social-signin", null, function(data) {
            //console.log(data);
            let facebookContainer = $(data).filter("div#facebook-container");
            if($("div#facebook-container").length == 0) {
                socialSigninContainer.hide();
                socialSigninContainer.after(facebookContainer);

                $(".cr-cookie-control-show").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    parentWindow.consentManager.show();
                    parentWindow.consentManager.showDetails();
                });

                $("#cr-cookie-control-allow-facebook-login").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    $("div#facebook-container").remove();
                    parentWindow.consentManager.setConsent("facebook-login", true);
                    parentWindow.consentManager.store();
                });
            }
        });
    }

    //LIEFERANTEN DE
    cr.lieferantenDE = new Object();
    cr.lieferantenDE.init = function(id) {
        if (window.consentManager.initStatusObj.lieferantenDE !== true) {
            let container = $("a[href^='https://www.lieferanten.de']");
            console.log(container);
            let url = "https://www.lieferanten.de/images/members/member.php?c=0&li=" + id;
            let image = $("#siegelImage").clone();
            $("#siegelImage").remove();
            image.attr("src", url);
            container.append(image);
            window.consentManager.initStatusObj.lieferantenDE = true;
        } else {
            console.log("Cookie constent for Lieferanten.de: already initialized");
        }
    }

    //APPLE
    cr.apple = new Object();
    cr.apple.initLogin = function() {
        $.getScript("https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js", function(data, textStatus, jqxhr) {
            appleInit();
            $("#appleid-signin").show();
            $("div#apple-container").remove();
        });
    }
    cr.apple.login_showBlockingMsg = function() {
        let socialSigninContainer = $("#appleid-signin");
        let parentWindow = window;
        let msg = $.get("/path/to/script/blocked-msg-social-signin", null, function(data) {
            //console.log(data);
            let appleContainer = $(data).filter("div#apple-container");
            if($("div#apple-container").length == 0) {
                socialSigninContainer.hide();
                socialSigninContainer.before(appleContainer);

                $(".cr-cookie-control-show").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    parentWindow.consentManager.show();
                    parentWindow.consentManager.showDetails();
                });

                $("#cr-cookie-control-allow-apple-login").on('click', function(ev) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    $("div#apple-container").remove();
                    parentWindow.consentManager.setConsent("apple-login", true);
                    parentWindow.consentManager.store();
                });
            }
        });
    }

    ...implement initializing options for various further services [END] ********/

    /**
     * Initializes the banner controls
     * - accept all
     * - reject all
     */
    priv.initMainButtons = function () {
        // Select all checkboxes when the user clicks on 'accept all'
        $(document).on('click', '.mod-cr-control-form button.mod-cr-accept-all', function () {
            cr.checkAll();
            cr.hide();
        });

        // Unselects all checkboxes when the user wants to store only the necessary cookies
        $(document).on('click', '.mod-cr-control-form button.mod-cr-reject', function () {
            cr.uncheckAll();
            priv.deleteAllCookies();
            cr.hide();
        });

        // Show the details settings form when the user wants to specify settings in detail
        $(document).on('click', '.mod-cr__mainbuttons .mod-cr-settings', function (event) {
            event.preventDefault();

            $('#mod-cr-settings-form')
                .toggle(200)
                .focus();
        });
    };

    /**
     * Initializes the cookie settings form details
     */
    priv.initDetails = function () {

        // Opens the description when the user clicks a category
        $(document).on('click', '.mod-cr-show-cookie-category-label', function (event) {
            let $description = jQuery(this).parent().next('.mod-cr-category-desc'),
                $form = $('#mod-cr-settings-form');

            event.preventDefault();

            // hide all but target
            $form.find('.mod-cr-category-desc')
                .not($description)
                .hide(100);
            $form.find('.mod-cr-show-cookie-category-label')
                .attr('aria-expanded', false);

            // toggle (show or hide) target
            $(this).attr('aria-expanded', !$description.is(':visible'));
            $description.toggle(100);
        });

        // When the user made his choice to accept cookies selected by himself
        // we hide the section, for cookie storage see the 'submit' event handler
        $(document).on('click', '.mod-cr-control-form .mod-cr-accept-settings', function () {
            cr.hide();
        });

        // toggle cookie category detail selection when toggling category
        $(document).on('change', '.mod-cr-control-form input[data-category-toggle]', function () {
            let $this = $(this);

            $('.mod-cr-control-form input[data-category=' + $this.data('category-toggle') + ']')
                .prop('checked', $this.is(':checked'));
        });

        // toggle cookie category when toggling a category cookie detail
        // uncheck category if last detail was unchecked
        // check category if at least one detail is checked
        $(document).on('change', '.mod-cr-control-form input[data-category-cookie-toggle]', function () {
            let $this = $(this),
                $checked = $('.mod-cr-control-form input[data-category=' + $this.data('category') + ']:checked');

            $('.mod-cr-control-form input[data-category-toggle=' + $this.data('category') + ']')
                .prop('checked', $checked.length);
        });

    };

    /**
     * Initializes some base functions
     */
    priv.initBasics = function () {

        // on form submission always store settings
        $(document).on('submit', '.mod-cr-control-form', function (event) {
            event.preventDefault();
            cr.store();
        });

        // Open the consent manager if the user opens it through an additional button
        $(document).on('click', '[data-role=cr-debug-toggle]', function (event) {
            event.preventDefault();
            cr.show();
        });
    };

    /**
     * Deletes all cookies (path = '/')
     */
    priv.deleteAllCookies = function () {
        let cookies = document.cookie.split("; "),
            i = 0;

        for (i = 0; i < cookies.length; i++) {
            let cookie = cookies[i],
                eqPos = cookie.indexOf('='),
                name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

            Cookies.remove(name);
        }
    };

    /**
     * Stores the cookies from given form
     */
    priv.storeCookies = function () {
        priv.cookieConsent = {};

        $('.mod-cr-control-form input[data-category-toggle]').each(function () {
            let $this = $(this),
                category = $this.data('category-toggle'),
                $details = $('.mod-cr-control-form input[data-category=' + category + ']:checked');

            priv.cookieConsent[category] = [];

            if (!$this.is(':checked')) {
                return;
            }

            $details.each(function () {
                priv.cookieConsent[category].push($(this).data('category-cookie-toggle'));
            });
        });

        priv.storeCookieData(priv.cookieConsent);
    };

    /**
     * Stores the data in consentManager's cookie
     *
     * @param {{}} data
     */
    priv.storeCookieData = function (data) {
        Cookies.set(cr.COOKIE_NAME, JSON.stringify(data), {
            path: '/',
            expires: 365
        });
    };

    // make available to global namespace
    // this is required as some possible future scripts, try to access consent
    // manager functions from child frames by i.e. using parent.window.consentManager
    window.cr = cr;
    window.consentManager = cr;

})(jQuery, Cookies);
